import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Box,
  Typography,
  Alert,
  Chip,
  Paper,
  useTheme
} from '@mui/material';
import {
  Add as AddIcon,
  TrendingUp as TrendingUpIcon,
  AttachMoney as MoneyIcon,
  Receipt as ReceiptIcon,
  Speed as SpeedIcon
} from '@mui/icons-material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';
import LoadingSpinner from './common/LoadingSpinner';

const Dashboard = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [period, setPeriod] = useState('month');
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.get(API_ENDPOINTS.invoiceDashboard(period));
      setDashboardData(response.data);
    } catch (err) {
      setError('Failed to fetch dashboard data');
      // Mock data for demonstration
      setDashboardData({
        period: period,
        totalSales: 125000.00,
        totalInvoices: 45,
        averageInvoiceValue: 2777.78,
        growthRate: 12.5,
        dailySales: [
          { date: '2024-01-01', sales: 2500, count: 2 },
          { date: '2024-01-02', sales: 3200, count: 3 },
          { date: '2024-01-03', sales: 1800, count: 1 },
          { date: '2024-01-04', sales: 4100, count: 4 },
          { date: '2024-01-05', sales: 2900, count: 2 }
        ],
        monthlySales: [
          { month: 'Jan', sales: 125000, count: 45 },
          { month: 'Feb', sales: 98000, count: 38 },
          { month: 'Mar', sales: 112000, count: 42 }
        ],
        yearlySales: [
          { year: '2022', sales: 1250000, count: 450 },
          { year: '2023', sales: 1380000, count: 520 },
          { year: '2024', sales: 335000, count: 125 }
        ]
      });
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const COLORS = [theme.palette.primary.main, theme.palette.secondary.main, theme.palette.success.main, theme.palette.warning.main, theme.palette.info.main];

  if (loading) {
    return <LoadingSpinner message="Loading dashboard data..." />;
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!dashboardData) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography>No data available</Typography>
      </Box>
    );
  }

  const StatCard = ({ title, value, icon, color, subtitle, trend }) => (
    <Card sx={{ 
      height: '100%',
      background: `linear-gradient(135deg, ${theme.palette[color].light}15 0%, ${theme.palette[color].main}08 100%)`,
      border: `1px solid ${theme.palette[color].light}30`,
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: theme.shadows[8],
        transition: 'all 0.3s ease-in-out',
      },
      transition: 'all 0.3s ease-in-out',
    }}>
      <CardContent sx={{ textAlign: 'center', p: 3 }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          mb: 2,
          color: theme.palette[color].main 
        }}>
          {icon}
        </Box>
        <Typography variant="h4" color={color} gutterBottom sx={{ fontWeight: 600 }}>
          {value}
        </Typography>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        )}
        {trend && (
          <Chip 
            label={`${trend > 0 ? '+' : ''}${trend}%`}
            color={trend > 0 ? 'success' : 'error'}
            size="small"
            sx={{ mt: 1 }}
          />
        )}
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
            Admin Dashboard
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Welcome to Moga Auto Invoicing System
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel id="period-select-label">Period</InputLabel>
            <Select
              labelId="period-select-label"
              value={period}
              label="Period"
              onChange={(e) => setPeriod(e.target.value)}
              size="small"
            >
              <MenuItem value="day">Daily</MenuItem>
              <MenuItem value="month">Monthly</MenuItem>
              <MenuItem value="year">Yearly</MenuItem>
            </Select>
          </FormControl>
          <Button 
            variant="contained" 
            startIcon={<AddIcon />}
            onClick={() => navigate('/invoice/new')}
            sx={{ 
              borderRadius: 2,
              px: 3,
              py: 1.5,
              fontWeight: 600,
              boxShadow: theme.shadows[4],
              '&:hover': {
                boxShadow: theme.shadows[8],
                transform: 'translateY(-2px)',
              },
              transition: 'all 0.3s ease-in-out',
            }}
          >
            Create New Invoice
          </Button>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Sales"
            value={`$${dashboardData.totalSales?.toLocaleString()}`}
            icon={<MoneyIcon sx={{ fontSize: 40 }} />}
            color="primary"
            subtitle={`Period: ${period}`}
            trend={dashboardData.growthRate}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Invoices"
            value={dashboardData.totalInvoices}
            icon={<ReceiptIcon sx={{ fontSize: 40 }} />}
            color="success"
            subtitle="Invoices generated"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Average Invoice"
            value={`$${dashboardData.averageInvoiceValue?.toFixed(2)}`}
            icon={<TrendingUpIcon sx={{ fontSize: 40 }} />}
            color="info"
            subtitle="Per invoice"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Growth Rate"
            value={`+${dashboardData.growthRate}%`}
            icon={<SpeedIcon sx={{ fontSize: 40 }} />}
            color="warning"
            subtitle="vs last period"
          />
        </Grid>
      </Grid>

      {/* Charts */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} lg={8}>
          <Card sx={{ height: '100%' }}>
            <CardHeader 
              title="Sales Trend" 
              subheader={`${period}ly sales performance`}
              titleTypographyProps={{ variant: 'h6', fontWeight: 600 }}
            />
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={dashboardData.dailySales}>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                  <XAxis dataKey="date" stroke={theme.palette.text.secondary} />
                  <YAxis stroke={theme.palette.text.secondary} />
                  <Tooltip 
                    formatter={(value) => [`$${value.toLocaleString()}`, 'Sales']}
                    contentStyle={{
                      backgroundColor: theme.palette.background.paper,
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 8,
                    }}
                  />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="sales" 
                    stroke={theme.palette.primary.main} 
                    strokeWidth={3}
                    dot={{ fill: theme.palette.primary.main, strokeWidth: 2, r: 4 }}
                    activeDot={{ r: 6, stroke: theme.palette.primary.main, strokeWidth: 2 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} lg={4}>
          <Card sx={{ height: '100%' }}>
            <CardHeader 
              title="Sales Distribution" 
              subheader="Monthly breakdown"
              titleTypographyProps={{ variant: 'h6', fontWeight: 600 }}
            />
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={dashboardData.monthlySales}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ month, sales }) => `${month}: $${sales.toLocaleString()}`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="sales"
                  >
                    {dashboardData.monthlySales.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value) => [`$${value.toLocaleString()}`, 'Sales']}
                    contentStyle={{
                      backgroundColor: theme.palette.background.paper,
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 8,
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Monthly Comparison */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12}>
          <Card>
            <CardHeader 
              title="Monthly Sales Comparison" 
              subheader="Sales vs Invoice count comparison"
              titleTypographyProps={{ variant: 'h6', fontWeight: 600 }}
            />
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={dashboardData.monthlySales}>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                  <XAxis dataKey="month" stroke={theme.palette.text.secondary} />
                  <YAxis stroke={theme.palette.text.secondary} />
                  <Tooltip 
                    formatter={(value) => [`$${value.toLocaleString()}`, 'Sales']}
                    contentStyle={{
                      backgroundColor: theme.palette.background.paper,
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 8,
                    }}
                  />
                  <Legend />
                  <Bar dataKey="sales" fill={theme.palette.primary.main} radius={[4, 4, 0, 0]} />
                  <Bar dataKey="count" fill={theme.palette.success.main} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Yearly Overview */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card>
            <CardHeader 
              title="Yearly Sales Overview" 
              subheader="Annual performance trends"
              titleTypographyProps={{ variant: 'h6', fontWeight: 600 }}
            />
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={dashboardData.yearlySales}>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                  <XAxis dataKey="year" stroke={theme.palette.text.secondary} />
                  <YAxis stroke={theme.palette.text.secondary} />
                  <Tooltip 
                    formatter={(value) => [`$${value.toLocaleString()}`, 'Sales']}
                    contentStyle={{
                      backgroundColor: theme.palette.background.paper,
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 8,
                    }}
                  />
                  <Legend />
                  <Bar dataKey="sales" fill={theme.palette.warning.main} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
